import {commandSearchCache} from '../cache/command-search-cache';
import {createGlobalScope, scopesEqual, toMountKey, toScopePath} from '../scope/keys';
import type {CommandSearchScope} from '../scope/types';
import type {
    CommandSearchContext,
    CommandSearchInvalidateOptions,
    CommandSearchMountOptions,
    CommandSearchProvider,
} from '../types';

interface MountedProvider {
    provider: CommandSearchProvider;
    scope: CommandSearchScope;
    cleanup?: () => void;
}

/**
 * Global registry for command-search provider definitions and scoped mount instances.
 */
class CommandSearchRegistryImpl {
    private readonly definitions = new Map<string, CommandSearchProvider>();
    private readonly mounted = new Map<string, MountedProvider>();
    private activeScope: CommandSearchScope = createGlobalScope('main');
    private scopeListeners = new Set<(scope: CommandSearchScope) => void>();

    /** Registers a provider definition (app bootstrap). */
    public register(provider: CommandSearchProvider): void {
        this.definitions.set(provider.id, provider);
    }

    /** Registers multiple provider definitions. */
    public registerMany(providers: CommandSearchProvider[]): void {
        providers.forEach((provider) => this.register(provider));
    }

    /** Returns a registered provider definition by id. */
    public getDefinition(providerId: string): CommandSearchProvider | undefined {
        return this.definitions.get(providerId);
    }

    /** Sets the active search scope used by the engine. */
    public setScope(scope: CommandSearchScope): void {
        this.activeScope = scope;
        this.scopeListeners.forEach((listener) => listener(scope));
    }

    /** Returns the current active search scope. */
    public getScope(): CommandSearchScope {
        return this.activeScope;
    }

    /** Subscribes to active scope changes. Returns an unsubscribe function. */
    public subscribeScope(listener: (scope: CommandSearchScope) => void): () => void {
        this.scopeListeners.add(listener);
        return () => this.scopeListeners.delete(listener);
    }

    /** Mounts a registered provider for a specific scope. */
    public mount(providerId: string, options: CommandSearchMountOptions): void {
        const provider = this.definitions.get(providerId);
        if (!provider) {
            console.warn(`[CommandSearchRegistry] Unknown provider "${providerId}"`);
            return;
        }

        const mountKey = toMountKey(providerId, options.scope);
        const existing = this.mounted.get(mountKey);
        existing?.cleanup?.();

        const ctx = this.buildContext('', options.scope);
        const cleanup = provider.onMount?.(ctx);

        this.mounted.set(mountKey, {
            provider,
            scope: options.scope,
            cleanup: typeof cleanup === 'function' ? cleanup : undefined,
        });
    }

    /** Unmounts a provider instance for a specific scope. */
    public unmount(providerId: string, options: CommandSearchMountOptions): void {
        const mountKey = toMountKey(providerId, options.scope);
        const existing = this.mounted.get(mountKey);
        if (!existing) {
            return;
        }
        existing.cleanup?.();
        this.mounted.delete(mountKey);
    }

    /** Unmounts all provider instances mounted for the given scope. */
    public unmountScope(scope: CommandSearchScope): void {
        for (const [mountKey, entry] of this.mounted.entries()) {
            if (scopesEqual(entry.scope, scope)) {
                entry.cleanup?.();
                this.mounted.delete(mountKey);
            }
        }
    }

    /** Returns mounted providers that are enabled for the given context. */
    public getActiveProviders(ctx: CommandSearchContext): CommandSearchProvider[] {
        const activePath = toScopePath(ctx.scope);
        const providers: CommandSearchProvider[] = [];

        for (const entry of this.mounted.values()) {
            const mountPath = toScopePath(entry.scope);
            const isGlobalMount = entry.scope.stack.length === 0
                && entry.scope.panelId === ctx.scope.panelId;
            const isExactMount = mountPath === activePath;

            if (!isGlobalMount && !isExactMount) {
                continue;
            }

            if (entry.provider.enabled && !entry.provider.enabled(ctx)) {
                continue;
            }
            providers.push(entry.provider);
        }

        return providers.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    }

    /** Builds a superseded-by map from active providers. */
    public getSupersedesMap(activeProviders: CommandSearchProvider[]): Map<string, Set<string>> {
        const map = new Map<string, Set<string>>();
        activeProviders.forEach((provider) => {
            if (provider.supersedes?.length) {
                map.set(provider.id, new Set(provider.supersedes));
            }
        });
        return map;
    }

    /** Invalidates cache entries matching the given options. */
    public invalidate(options: CommandSearchInvalidateOptions): void {
        if (options.scopePath) {
            commandSearchCache.invalidateByPrefix(`${options.scopePath}::`);
            return;
        }

        if (options.scope) {
            commandSearchCache.invalidateByPrefix(`${toScopePath(options.scope)}::`);
            return;
        }

        if (options.panelId && options.scopeType) {
            const panelPrefix = `${options.panelId}/`;
            const typeSegment = `/${options.scopeType}:`;
            commandSearchCache.invalidateMatching((key) =>
                key.startsWith(panelPrefix) && key.includes(typeSegment));
            return;
        }

        if (options.panelId) {
            commandSearchCache.invalidateByPrefix(`${options.panelId}/`);
            return;
        }

        if (options.providerId && options.scope) {
            commandSearchCache.invalidateByPrefix(`${toScopePath(options.scope)}::${options.providerId}::`);
        }
    }

    /** Invalidates cache for a specific provider in a scope. */
    public invalidateProvider(providerId: string, scope: CommandSearchScope): void {
        this.invalidate({providerId, scope});
    }

    /** Returns all mounted provider ids for debugging. */
    public getMountedKeys(): string[] {
        return [...this.mounted.keys()];
    }

    /** Clears all mounts and definitions (useful in tests). */
    public reset(): void {
        for (const entry of this.mounted.values()) {
            entry.cleanup?.();
        }
        this.mounted.clear();
        this.definitions.clear();
        this.activeScope = createGlobalScope('main');
        commandSearchCache.clear();
    }

    /** Builds a search context for provider calls. */
    public buildContext(
        query: string,
        scope?: CommandSearchScope,
        navigate?: CommandSearchContext['navigate'],
        abortController?: AbortController,
        services?: Record<string, unknown>,
    ): CommandSearchContext {
        const resolvedScope = scope ?? this.activeScope;
        return {
            scope: resolvedScope,
            scopePath: toScopePath(resolvedScope),
            query,
            navigate: navigate ?? (() => undefined),
            abortController: abortController ?? new AbortController(),
            services,
        };
    }
}

export const CommandSearchRegistry = new CommandSearchRegistryImpl();
